// Meldet die Bank zu einer eingetippten IBAN. Als Hook, weil es zwei IBAN-Felder
// gibt, die sich optisch, aber nicht im Verhalten unterscheiden: `IbanEingabe`
// (rohes Input für Masken mit eigenem Layout) und `IbanInput` (Refined-Look
// über ValidatedField).
//
// WICHTIG — warum das an der EINGABE hängt und nicht am Wert:
//
// Bis 09/2026 lief die Erkennung in einem Effekt über den Feldwert. Damit feuerte
// sie auch beim bloßen ÖFFNEN einer Kundenakte: Stand dort eine deutsche IBAN
// und war BIC oder Kreditinstitut leer, trug die Maske die Werte von selbst ein.
// Für die Dirty-Prüfung der Akte (ein Vergleich des ganzen Formularzustands
// gegen die Momentaufnahme beim Laden) ist das nicht von einer echten Eingabe zu
// unterscheiden — die Speicherleiste ging auf, obwohl niemand etwas angefasst
// hatte, und ließ sich bis zum Speichern nicht mehr zuklappen.
//
// Deshalb liefert der Hook jetzt eine Funktion, die das Feld beim Tippen und
// Einfügen ruft. Was beim Laden schon dasteht, ist der gespeicherte Stand und
// wird nicht angerührt.

import { useCallback, useRef } from "react";
import { bankleitzahlAusIban } from "../validation/iban";
import { bankZuBankleitzahl, type BankTreffer } from "./bankleitzahlen";

/**
 * Gibt eine Prüffunktion zurück, die `onBankErkannt` höchstens einmal je
 * Bankleitzahl aufruft.
 *
 * Die Merk-Referenz ist nötig, weil jeder weitere Tastendruck an derselben IBAN
 * sonst erneut meldete — und ein BIC, den der Anwender gerade von Hand
 * korrigiert hat, sofort wieder zurückgesetzt würde.
 */
export function useBankAusIban(
  onBankErkannt?: (bank: BankTreffer) => void,
): (iban: string) => void {
  const gemeldet = useRef<string | null>(null);
  // Die Aufrufer geben die Rückmeldung meist inline mit; als Abhängigkeit
  // erzeugte sie bei jedem Render eine neue Prüffunktion.
  const rueckmeldung = useRef(onBankErkannt);
  rueckmeldung.current = onBankErkannt;

  return useCallback((iban: string) => {
    const blz = bankleitzahlAusIban(String(iban ?? ""));
    if (!blz) {
      // Wird die IBAN wieder unvollständig, darf dieselbe Bank später erneut
      // gemeldet werden — sonst bliebe ein versehentlich geleertes Feld leer.
      gemeldet.current = null;
      return;
    }
    if (gemeldet.current === blz) return;
    void bankZuBankleitzahl(blz).then((treffer) => {
      if (!treffer) return;
      gemeldet.current = blz;
      rueckmeldung.current?.(treffer);
    });
  }, []);
}
